import React, { useMemo } from 'react';
import { Avatar, Box, Button, Chip, Grid, Stack, Typography } from '@mui/material';
import {
  AssignmentTurnedIn,
  CardMembership,
  Dashboard,
  People,
  SupportAgent,
  WarningAmber,
} from '@mui/icons-material';
import {
  Area,
  AreaChart,
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  Legend,
  Line,
  LineChart,
  Pie,
  PieChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { format, subDays } from 'date-fns';
import { waitlistAPI } from '../../../services/api';
import { tokens } from '../../../theme';
import {
  computeCustomerAnalytics,
  computeOperationsAnalytics,
  computeReviewAnalytics,
  computeSubscriptionAnalytics,
} from '../../../utils/adminAnalytics';
import UserDashboardSettings from '../UserDashboardSettings';
import landingForest from '../../../assets/landing-forest.png';
import cardMountains from '../../../assets/card-mountains.png';
import ctaBackground from '../../../assets/cta-background.png';
import {
  AnalyticsHero,
  AnalyticsScreen,
  ChartPanel,
  InsightCallout,
  MetricTile,
  analyticsTokens,
  chartTooltipStyle,
  panelSx,
} from './shared';

const PIE_COLORS = ['#a9bf7a', '#c98b67', '#8ec8d7', '#d0b47e', '#e67c73', '#d58a5f'];

function buildTrendData(users, tickets, subscriptions) {
  const labels = Array.from({ length: 30 }).map((_, index) => format(subDays(new Date(), 29 - index), 'MMM d'));
  const map = Object.fromEntries(labels.map((label) => [label, { label, customers: 0, tickets: 0, premium: 0 }]));
  users.forEach((user) => {
    if (!user.createdAt) return;
    const label = format(new Date(user.createdAt), 'MMM d');
    if (map[label]) map[label].customers += 1;
  });
  tickets.forEach((ticket) => {
    if (!ticket.createdAt) return;
    const label = format(new Date(ticket.createdAt), 'MMM d');
    if (map[label]) map[label].tickets += 1;
  });
  subscriptions.forEach((subscription) => {
    if (!subscription.subscriptionLastUpdated) return;
    const label = format(new Date(subscription.subscriptionLastUpdated), 'MMM d');
    if (map[label] && subscription.isPremium) map[label].premium += 1;
  });
  let customersRunning = 0;
  let ticketsRunning = 0;
  let premiumRunning = 0;
  return labels.map((label) => {
    customersRunning += map[label].customers;
    ticketsRunning += map[label].tickets;
    premiumRunning += map[label].premium;
    return { label, customers: customersRunning, tickets: ticketsRunning, premium: premiumRunning };
  });
}

export function OverviewAnalytics({ stats, users, subscriptions, tickets, submissions, onOpenOperations }) {
  const premiumSubscribers = subscriptions.filter((s) => s.isPremium && s.status === 'active').length;
  const unresolvedTickets = tickets.filter((t) => t.status !== 'resolved' && t.status !== 'closed');
  const trendData = useMemo(() => buildTrendData(users, tickets, subscriptions), [users, tickets, subscriptions]);

  return (
    <AnalyticsScreen>
      <AnalyticsHero
        title="Platform Overview"
        subtitle="Executive analytics across growth, revenue, support pressure, and content pipeline health."
        backgroundImage={landingForest}
        chips={[`${(stats?.totalUsers || users.length).toLocaleString()} customers`]}
        actions={<Button variant="contained" onClick={onOpenOperations}>Open workspace</Button>}
      />
      <Grid container spacing={1.5} sx={{ mb: 1.5 }}>
        <Grid item xs={6} md={4} lg={2}><MetricTile label="Total customers" value={(stats?.totalUsers || users.length).toLocaleString()} icon={<People fontSize="small" />} /></Grid>
        <Grid item xs={6} md={4} lg={2}><MetricTile label="Active (30d)" value={(stats?.activeUsers30d || 0).toLocaleString()} icon={<Dashboard fontSize="small" />} accent={tokens.accentStrong} /></Grid>
        <Grid item xs={6} md={4} lg={2}><MetricTile label="Premium" value={premiumSubscribers.toLocaleString()} icon={<CardMembership fontSize="small" />} /></Grid>
        <Grid item xs={6} md={4} lg={2}><MetricTile label="Open tickets" value={unresolvedTickets.length.toString()} icon={<SupportAgent fontSize="small" />} accent="#d49063" /></Grid>
        <Grid item xs={6} md={4} lg={2}><MetricTile label="Content queue" value={submissions.length.toString()} icon={<AssignmentTurnedIn fontSize="small" />} accent="#d0c37d" /></Grid>
      </Grid>
      <Grid container spacing={1.5}>
        <Grid item xs={12} lg={8}>
          <ChartPanel title="Growth & operations trend" subtitle="30-day cumulative customers, premium adds, and tickets">
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={trendData}>
                <defs>
                  <linearGradient id="custFill" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#a9bf7a" stopOpacity={0.3} />
                    <stop offset="100%" stopColor="#a9bf7a" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid stroke="rgba(255,255,255,0.08)" vertical={false} />
                <XAxis dataKey="label" stroke={tokens.textMuted} tickLine={false} axisLine={false} />
                <YAxis stroke={tokens.textMuted} tickLine={false} axisLine={false} />
                <Tooltip contentStyle={chartTooltipStyle} />
                <Legend />
                <Area type="monotone" dataKey="customers" stroke={tokens.accentStrong} fill="url(#custFill)" strokeWidth={2} name="Customers" />
                <Line type="monotone" dataKey="premium" stroke="#e5d7b4" strokeWidth={1.8} dot={false} name="Premium" />
                <Line type="monotone" dataKey="tickets" stroke={tokens.clay} strokeWidth={1.6} dot={false} name="Tickets" />
              </AreaChart>
            </ResponsiveContainer>
          </ChartPanel>
        </Grid>
        <Grid item xs={12} lg={4}>
          <Box sx={panelSx({ p: 2, height: '100%' })}>
            <Typography variant="h5" sx={{ color: tokens.cream, mb: 1.5 }}>Urgent alerts</Typography>
            <Stack spacing={0.8}>
              {unresolvedTickets.filter((t) => t.priority === 'critical' || t.priority === 'high').slice(0, 5).map((ticket) => (
                <Box key={ticket.id} sx={{ p: 1, border: `1px solid ${analyticsTokens.border}`, backgroundColor: 'rgba(255,255,255,0.02)' }}>
                  <Typography variant="body2" sx={{ color: tokens.cream }} noWrap>{ticket.subject}</Typography>
                  <Typography variant="caption" sx={{ color: tokens.textMuted }}>{ticket.priority} · {ticket.userEmail}</Typography>
                </Box>
              ))}
              {unresolvedTickets.length === 0 && <Typography variant="body2" sx={{ color: tokens.textMuted }}>No urgent alerts.</Typography>}
            </Stack>
          </Box>
        </Grid>
        <Grid item xs={12}>
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'repeat(4, 1fr)' }, gap: 1, ...panelSx(), p: 2 }}>
            {[
              ['API Services', 'Healthy'],
              ['Support queue', `${unresolvedTickets.length} open`],
              ['Food review', `${submissions.length} pending`],
              ['Subscriptions', `${premiumSubscribers} active`],
            ].map(([label, value]) => (
              <Box key={label}>
                <Typography variant="caption" sx={{ color: tokens.textMuted }}>{label}</Typography>
                <Typography variant="body1" sx={{ color: tokens.cream }}>{value}</Typography>
              </Box>
            ))}
          </Box>
        </Grid>
      </Grid>
    </AnalyticsScreen>
  );
}

export function OperationsAnalytics({ tickets, submissions, subscriptions, children }) {
  const data = useMemo(() => computeOperationsAnalytics(tickets, submissions, subscriptions), [tickets, submissions, subscriptions]);

  return (
    <AnalyticsScreen>
      <AnalyticsHero
        title="Operations Analytics"
        subtitle="Case velocity, priority distribution, and workload composition across support, review, and billing."
        backgroundImage={cardMountains}
        chips={[`${data.totalCases} active cases`, `${data.critical} high priority`]}
      />
      <Grid container spacing={1.5} sx={{ mb: 1.5 }}>
        <Grid item xs={6} md={3}><MetricTile label="Open tickets" value={data.openTickets} icon={<SupportAgent fontSize="small" />} /></Grid>
        <Grid item xs={6} md={3}><MetricTile label="Pending reviews" value={data.pendingReviews} icon={<AssignmentTurnedIn fontSize="small" />} accent="#d0c37d" /></Grid>
        <Grid item xs={6} md={3}><MetricTile label="Subscription issues" value={data.subscriptionIssues} icon={<CardMembership fontSize="small" />} accent={tokens.clay} /></Grid>
        <Grid item xs={6} md={3}><MetricTile label="Critical/high" value={data.critical} icon={<WarningAmber fontSize="small" />} accent={tokens.danger} /></Grid>
      </Grid>
      <Grid container spacing={1.5} sx={{ mb: 2 }}>
        <Grid item xs={12} md={6}>
          <ChartPanel title="Ticket velocity" subtitle="New tickets per day (14d)">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={data.ticketTrend}>
                <CartesianGrid stroke="rgba(255,255,255,0.08)" vertical={false} />
                <XAxis dataKey="label" stroke={tokens.textMuted} tickLine={false} axisLine={false} />
                <YAxis stroke={tokens.textMuted} tickLine={false} axisLine={false} />
                <Tooltip contentStyle={chartTooltipStyle} />
                <Bar dataKey="tickets" fill={tokens.clay} />
              </BarChart>
            </ResponsiveContainer>
          </ChartPanel>
        </Grid>
        <Grid item xs={12} md={3}>
          <ChartPanel title="Priority mix" subtitle="Open ticket priorities" height={280}>
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie data={data.priorityBreakdown} dataKey="count" nameKey="priority" innerRadius={50} outerRadius={85}>
                  {data.priorityBreakdown.map((_, i) => <Cell key={i} fill={PIE_COLORS[i % PIE_COLORS.length]} />)}
                </Pie>
                <Tooltip contentStyle={chartTooltipStyle} />
                <Legend />
              </PieChart>
            </ResponsiveContainer>
          </ChartPanel>
        </Grid>
        <Grid item xs={12} md={3}>
          <ChartPanel title="Case composition" subtitle="Workload by type" height={280}>
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={data.caseMix} layout="vertical">
                <CartesianGrid stroke="rgba(255,255,255,0.08)" horizontal={false} />
                <XAxis type="number" stroke={tokens.textMuted} tickLine={false} axisLine={false} />
                <YAxis type="category" dataKey="type" stroke={tokens.textMuted} tickLine={false} axisLine={false} width={90} />
                <Tooltip contentStyle={chartTooltipStyle} />
                <Bar dataKey="count" fill={tokens.accentStrong} />
              </BarChart>
            </ResponsiveContainer>
          </ChartPanel>
        </Grid>
      </Grid>
      {children}
    </AnalyticsScreen>
  );
}

export function CustomersAnalytics({ users, onSelectCustomer }) {
  const data = useMemo(() => computeCustomerAnalytics(users), [users]);

  return (
    <AnalyticsScreen>
      <AnalyticsHero
        title="Customer Analytics"
        subtitle="Acquisition trends, segment distribution, verification rates, and premium penetration."
        backgroundImage={cardMountains}
        chips={[`${data.premiumRate}% premium rate`, `${data.activationRate}% verified`]}
      />
      <Grid container spacing={1.5} sx={{ mb: 1.5 }}>
        <Grid item xs={6} md={3}><MetricTile label="Total customers" value={data.total} icon={<People fontSize="small" />} /></Grid>
        <Grid item xs={6} md={3}><MetricTile label="Premium" value={data.premium} footnote={`${data.premiumRate}% of base`} icon={<CardMembership fontSize="small" />} /></Grid>
        <Grid item xs={6} md={3}><MetricTile label="Verified" value={data.verified} footnote={`${data.activationRate}% rate`} accent="#8ec8d7" /></Grid>
        <Grid item xs={6} md={3}><MetricTile label="Suspended" value={data.suspended} accent={tokens.danger} icon={<WarningAmber fontSize="small" />} /></Grid>
      </Grid>
      <Grid container spacing={1.5}>
        <Grid item xs={12} lg={8}>
          <ChartPanel title="Customer growth" subtitle="Daily signups and cumulative base">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={data.signupTrend}>
                <CartesianGrid stroke="rgba(255,255,255,0.08)" vertical={false} />
                <XAxis dataKey="label" stroke={tokens.textMuted} tickLine={false} axisLine={false} />
                <YAxis stroke={tokens.textMuted} tickLine={false} axisLine={false} />
                <Tooltip contentStyle={chartTooltipStyle} />
                <Legend />
                <Bar dataKey="signups" fill={tokens.clay} name="Daily signups" />
                <Line type="monotone" dataKey="cumulative" stroke={tokens.accentStrong} strokeWidth={2.2} name="Cumulative" dot={false} />
              </LineChart>
            </ResponsiveContainer>
          </ChartPanel>
        </Grid>
        <Grid item xs={12} lg={4}>
          <ChartPanel title="Segment mix" subtitle="Premium, free, and suspended">
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie data={data.segments} dataKey="value" nameKey="name" innerRadius={55} outerRadius={90}>
                  {data.segments.map((_, i) => <Cell key={i} fill={PIE_COLORS[i % PIE_COLORS.length]} />)}
                </Pie>
                <Tooltip contentStyle={chartTooltipStyle} />
                <Legend />
              </PieChart>
            </ResponsiveContainer>
          </ChartPanel>
        </Grid>
        <Grid item xs={12}>
          <Box sx={panelSx({ p: 0 })}>
            <Box sx={{ p: 2, borderBottom: `1px solid ${analyticsTokens.border}` }}>
              <Typography variant="h5" sx={{ color: tokens.cream }}>Customer directory</Typography>
            </Box>
            <Box sx={{ maxHeight: 360, overflowY: 'auto' }}>
              {users.slice(0, 20).map((user) => (
                <Box key={user.id} onClick={() => onSelectCustomer?.(user)} sx={{ p: 1.5, borderBottom: `1px solid ${analyticsTokens.border}`, cursor: onSelectCustomer ? 'pointer' : 'default', '&:hover': { backgroundColor: 'rgba(169,191,122,0.06)' } }}>
                  <Stack direction="row" justifyContent="space-between" alignItems="center">
                    <Stack direction="row" spacing={1.25} alignItems="center">
                      <Avatar sx={{ width: 34, height: 34, bgcolor: 'rgba(255,255,255,0.08)' }}>{(user.name || user.email || 'U')[0].toUpperCase()}</Avatar>
                      <Box>
                        <Typography variant="body2" sx={{ color: tokens.cream }}>{user.name || 'Unnamed'}</Typography>
                        <Typography variant="caption" sx={{ color: tokens.textMuted }}>{user.email}</Typography>
                      </Box>
                    </Stack>
                    <Chip label={user.subscription?.isPremium ? 'Premium' : 'Free'} size="small" sx={{ borderRadius: 2 }} />
                  </Stack>
                </Box>
              ))}
            </Box>
          </Box>
        </Grid>
      </Grid>
    </AnalyticsScreen>
  );
}

export function SubscriptionsAnalytics({ subscriptions, users }) {
  const data = useMemo(() => computeSubscriptionAnalytics(subscriptions, users), [subscriptions, users]);

  return (
    <AnalyticsScreen>
      <AnalyticsHero
        title="Subscription Analytics"
        subtitle="Churn, platform distribution, and upcoming renewals."
        backgroundImage={landingForest}
        chips={[`${data.churnRate}% churn rate`]}
      />
      <Grid container spacing={1.5} sx={{ mb: 1.5 }}>
        <Grid item xs={6} md={3}><MetricTile label="Active premium" value={data.active} icon={<CardMembership fontSize="small" />} /></Grid>
        <Grid item xs={6} md={3}><MetricTile label="Grace period" value={data.grace} accent="#d0b47e" /></Grid>
        <Grid item xs={6} md={3}><MetricTile label="Trial active" value={data.trialActive} accent={tokens.accentStrong} /></Grid>
        <Grid item xs={6} md={3}><MetricTile label="Cancelled" value={data.cancelled} accent={tokens.danger} /></Grid>
        <Grid item xs={6} md={3}><MetricTile label="Expired" value={data.expired} accent={tokens.clay} /></Grid>
        <Grid item xs={6} md={3}><MetricTile label="Unverified" value={data.unverified} accent="#d0c37d" /></Grid>
      </Grid>
      <Grid container spacing={1.5}>
        <Grid item xs={12} md={6}>
          <ChartPanel title="Status breakdown" subtitle="Subscription states">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={data.statusBreakdown}>
                <CartesianGrid stroke="rgba(255,255,255,0.08)" vertical={false} />
                <XAxis dataKey="status" stroke={tokens.textMuted} tickLine={false} axisLine={false} />
                <YAxis stroke={tokens.textMuted} tickLine={false} axisLine={false} />
                <Tooltip contentStyle={chartTooltipStyle} />
                <Bar dataKey="count" fill={tokens.accentStrong} />
              </BarChart>
            </ResponsiveContainer>
          </ChartPanel>
        </Grid>
        <Grid item xs={12} md={6}>
          <ChartPanel title="Billing platform" subtitle="Subscriptions by store">
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie data={data.platformBreakdown} dataKey="count" nameKey="name" innerRadius={50} outerRadius={85}>
                  {data.platformBreakdown.map((_, i) => <Cell key={i} fill={PIE_COLORS[i % PIE_COLORS.length]} />)}
                </Pie>
                <Tooltip contentStyle={chartTooltipStyle} />
                <Legend />
              </PieChart>
            </ResponsiveContainer>
          </ChartPanel>
        </Grid>
        <Grid item xs={12} md={6}>
          <ChartPanel title="Entitlement mix" subtitle="Premium, trial, and free">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={data.entitlementBreakdown}>
                <CartesianGrid stroke="rgba(255,255,255,0.08)" vertical={false} />
                <XAxis dataKey="name" stroke={tokens.textMuted} tickLine={false} axisLine={false} />
                <YAxis stroke={tokens.textMuted} tickLine={false} axisLine={false} />
                <Tooltip contentStyle={chartTooltipStyle} />
                <Bar dataKey="count" fill={tokens.clay} />
              </BarChart>
            </ResponsiveContainer>
          </ChartPanel>
        </Grid>
        <Grid item xs={12}>
          <Box sx={panelSx({ p: 2 })}>
            <Typography variant="h5" sx={{ color: tokens.cream, mb: 1.5 }}>Upcoming renewals</Typography>
            <Stack spacing={0.8}>
              {data.renewalTimeline.map((item) => (
                <Box key={`${item.customer}-${item.expires}`} sx={{ p: 1.1, border: `1px solid ${analyticsTokens.border}`, backgroundColor: 'rgba(255,255,255,0.02)' }}>
                  <Stack direction="row" justifyContent="space-between">
                    <Typography variant="body2" sx={{ color: tokens.cream }}>{item.customer}</Typography>
                    <Chip label={item.status} size="small" sx={{ borderRadius: 2, textTransform: 'capitalize' }} />
                  </Stack>
                  <Typography variant="caption" sx={{ color: tokens.textMuted }}>
                    Renews {item.expires} · {item.entitlement} · {item.store}
                    {item.lastVerifiedAt ? ` · verified ${item.lastVerifiedAt}` : ''}
                  </Typography>
                </Box>
              ))}
              {data.renewalTimeline.length === 0 && <InsightCallout title="No renewal data" body="Subscription expiry dates will appear here as records are synced." />}
            </Stack>
          </Box>
        </Grid>
      </Grid>
    </AnalyticsScreen>
  );
}

export function ReviewAnalytics({ submissions, onApprove, onReject, updating }) {
  const data = useMemo(() => computeReviewAnalytics(submissions), [submissions]);

  return (
    <AnalyticsScreen>
      <AnalyticsHero
        title="Content Review Analytics"
        subtitle="Submission volume, approval rates, calorie distribution, and category breakdown."
        backgroundImage={ctaBackground}
        chips={[`${data.approvalRate}% approval rate`, `${data.pending} pending`]}
      />
      <Grid container spacing={1.5} sx={{ mb: 1.5 }}>
        <Grid item xs={6} md={3}><MetricTile label="Pending" value={data.pending} icon={<AssignmentTurnedIn fontSize="small" />} accent="#d0c37d" /></Grid>
        <Grid item xs={6} md={3}><MetricTile label="Approved" value={data.approved} accent={tokens.accentStrong} /></Grid>
        <Grid item xs={6} md={3}><MetricTile label="Rejected" value={data.rejected} accent={tokens.danger} /></Grid>
        <Grid item xs={6} md={3}><MetricTile label="Total reviewed" value={data.total} /></Grid>
      </Grid>
      <Grid container spacing={1.5}>
        <Grid item xs={12} md={8}>
          <ChartPanel title="Submission volume" subtitle="Daily submissions (14d)">
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={data.volumeTrend}>
                <CartesianGrid stroke="rgba(255,255,255,0.08)" vertical={false} />
                <XAxis dataKey="label" stroke={tokens.textMuted} tickLine={false} axisLine={false} />
                <YAxis stroke={tokens.textMuted} tickLine={false} axisLine={false} />
                <Tooltip contentStyle={chartTooltipStyle} />
                <Area type="monotone" dataKey="submissions" stroke="#d0c37d" fill="#d0c37d" fillOpacity={0.15} strokeWidth={2} />
              </AreaChart>
            </ResponsiveContainer>
          </ChartPanel>
        </Grid>
        <Grid item xs={12} md={4}>
          <ChartPanel title="Calorie density" subtitle="Submissions by kcal/100g bucket">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={[
                { bucket: '<100', count: data.calorieBuckets.low },
                { bucket: '100-300', count: data.calorieBuckets.mid },
                { bucket: '300+', count: data.calorieBuckets.high },
              ]}>
                <CartesianGrid stroke="rgba(255,255,255,0.08)" vertical={false} />
                <XAxis dataKey="bucket" stroke={tokens.textMuted} tickLine={false} axisLine={false} />
                <YAxis stroke={tokens.textMuted} tickLine={false} axisLine={false} />
                <Tooltip contentStyle={chartTooltipStyle} />
                <Bar dataKey="count" fill={tokens.clay} />
              </BarChart>
            </ResponsiveContainer>
          </ChartPanel>
        </Grid>
        <Grid item xs={12} md={6}>
          <ChartPanel title="Top categories" subtitle="Most submitted food categories" height={260}>
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={data.categoryBreakdown} layout="vertical">
                <CartesianGrid stroke="rgba(255,255,255,0.08)" horizontal={false} />
                <XAxis type="number" stroke={tokens.textMuted} tickLine={false} axisLine={false} />
                <YAxis type="category" dataKey="name" stroke={tokens.textMuted} tickLine={false} axisLine={false} width={100} />
                <Tooltip contentStyle={chartTooltipStyle} />
                <Bar dataKey="count" fill={tokens.accentStrong} />
              </BarChart>
            </ResponsiveContainer>
          </ChartPanel>
        </Grid>
        <Grid item xs={12} md={6}>
          <Box sx={panelSx({ p: 2, maxHeight: 320, overflowY: 'auto' })}>
            <Typography variant="h5" sx={{ color: tokens.cream, mb: 1.5 }}>Review queue</Typography>
            <Stack spacing={1}>
              {submissions.filter((s) => (s.status || 'pending') === 'pending').slice(0, 8).map((submission) => (
                <Box key={submission.id || submission._id} sx={{ p: 1.2, border: `1px solid ${analyticsTokens.border}`, backgroundColor: 'rgba(255,255,255,0.02)' }}>
                  <Typography variant="body2" sx={{ color: tokens.cream }}>{submission.productName}</Typography>
                  <Typography variant="caption" sx={{ color: tokens.textMuted, display: 'block', mb: 1 }}>{submission.barcode} · {submission.caloriesPer100g} kcal/100g</Typography>
                  <Stack direction="row" spacing={1}>
                    <Button size="small" variant="contained" disabled={updating} onClick={() => onApprove?.(submission)}>Approve</Button>
                    <Button size="small" variant="outlined" color="error" disabled={updating} onClick={() => onReject?.(submission)}>Reject</Button>
                  </Stack>
                </Box>
              ))}
            </Stack>
          </Box>
        </Grid>
      </Grid>
    </AnalyticsScreen>
  );
}

export function WaitlistAnalytics() {
  const [entries, setEntries] = React.useState([]);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState('');

  React.useEffect(() => {
    let mounted = true;
    (async () => {
      setLoading(true);
      setError('');
      try {
        const res = await waitlistAPI.list({ limit: 200 });
        if (mounted) setEntries(res.data?.data?.items || []);
      } catch (err) {
        if (mounted) setError(err.response?.data?.message || err.message || 'Failed to load waitlist');
      } finally {
        if (mounted) setLoading(false);
      }
    })();
    return () => { mounted = false; };
  }, []);

  const handleExport = async () => {
    try {
      const res = await waitlistAPI.exportCsv();
      const blob = new Blob([res.data], { type: 'text/csv' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `fasttime-waitlist-${new Date().toISOString().slice(0, 10)}.csv`;
      link.click();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      setError(err.response?.data?.message || err.message || 'Export failed');
    }
  };

  return (
    <AnalyticsScreen>
      <AnalyticsHero
        title="Waitlist"
        subtitle="Early access signups from the marketing site."
        backgroundImage={landingForest}
        chips={[`${entries.length} entries`]}
        actions={<Button variant="contained" onClick={handleExport}>Export CSV</Button>}
      />
      {error && <Typography variant="body2" sx={{ color: tokens.danger, mb: 2 }}>{error}</Typography>}
      <Box sx={panelSx({ p: 2 })}>
        {loading ? (
          <Typography variant="body2" sx={{ color: tokens.textMuted }}>Loading…</Typography>
        ) : entries.length === 0 ? (
          <Typography variant="body2" sx={{ color: tokens.textMuted }}>No waitlist entries yet.</Typography>
        ) : (
          <Stack spacing={0.8}>
            {entries.slice(0, 50).map((entry) => (
              <Box key={entry.id || entry.email} sx={{ p: 1.1, border: `1px solid ${analyticsTokens.border}`, backgroundColor: 'rgba(255,255,255,0.02)' }}>
                <Typography variant="body2" sx={{ color: tokens.cream }}>{entry.email}</Typography>
                <Typography variant="caption" sx={{ color: tokens.textMuted }}>
                  {entry.name || 'No name'}{entry.createdAt ? ` · ${format(new Date(entry.createdAt), 'MMM d, yyyy')}` : ''}
                </Typography>
              </Box>
            ))}
          </Stack>
        )}
      </Box>
    </AnalyticsScreen>
  );
}

export function AdminSettingsAnalytics() {
  return <UserDashboardSettings />;
}

export const ADMIN_ANALYTICS_SECTIONS = ['overview', 'operations', 'customers', 'subscriptions', 'review', 'waitlist', 'system', 'settings'];
